import { useDaily } from '@daily-co/daily-react';
import { useCallback, useState } from 'react';

import { useCallState } from '../../contexts/CallProvider';
import { isSafari } from '../../lib/browserConfig';

interface GrantAccessArgs {
  onFail?(e: any): void;
  onSuccess?(): void;
}

/**
 * Utility hook to consolidate logic around unblocking permissions in Safari.
 */
export const useSafariUnblockPermissions = () => {
  const daily = useDaily();
  const { disableAudio, mode } = useCallState();
  const [safariRetryCount, setSafariRetryCount] = useState(0);

  /**
   * Executes logic to grant cam & mic permissions in Safari.
   * Reloads the page in direct-link calls.
   * In embedded calls we'll call getUserMedia manually with a "fake" deviceId,
   * in order to generate new gUM constraints every time we call grantAccess.
   * Safari allows up to 3 attempts, after that all calls to gUM will be ignored
   * until the user refreshes the page.
   */
  const grantAccess = useCallback(
    async ({ onFail, onSuccess }: GrantAccessArgs = {}) => {
      if (mode === 'direct-link') {
        window.location.reload();
      } else {
        try {
          if (isSafari(14)) {
            await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: {
                  ideal: safariRetryCount.toString(),
                },
              },
            });
          }
          daily.setLocalVideo(true);
          if (!disableAudio) daily.setLocalAudio(true);
          onSuccess?.();
        } catch (e) {
          onFail?.(e);
          setSafariRetryCount((i) => i + 1);
        }
      }
    },
    [daily, disableAudio, mode, safariRetryCount]
  );

  return { grantAccess };
};
